import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useCompanyStats, useTestimonials } from '../hooks/useApi';
import { 
  BarChart3, 
  FileCheck, 
  Users, 
  Shield, 
  TrendingUp, 
  Clock,
  CheckCircle,
  ArrowRight,
  Star,
  Loader2
} from 'lucide-react';

const HomePage = () => {
  // Fetch real data from API
  const { data: stats, loading: statsLoading } = useCompanyStats();
  const { data: apiTestimonials, loading: testimonialsLoading } = useTestimonials(true);
  const services = [
    {
      icon: <FileCheck className="w-8 h-8 text-blue-600" />,
      title: "Audit and Capital",
      description: "Seamless, self-learning freight auditing and payment services tailored to your unique business processes and modelled to improve your working capital efficiency."
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-teal-600" />,
      title: "Data and BPO",
      description: "Streamlined back-office support and transaction management aligned with your systems, allowing you to concentrate on your core business priorities."
    },
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Insights and AI",
      description: "Access all your data in real-time, analyze internal and third-party performance, and benchmark your market position."
    },
    {
      icon: <Shield className="w-8 h-8 text-teal-600" />,
      title: "PBX Projects",
      description: "Consulting services to help you drive innovation while controlling your spend, so that you can focus on your core mission and strategy."
    }
  ];

  const benefits = [
    "Implement automation to improve efficiencies",
    "Map and outsource internal processes to consistently capture opportunities for improvement",
    "Improve Vendor and Customer relationship management",
    "Access to real-time performance insights",
    "24/7 dedicated support team"
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      company: "Global Manufacturing Inc.",
      text: "PBX Solutions transformed our invoice processing. We've saved countless hours and significantly reduced errors.",
      rating: 5
    },
    {
      name: "Michael Chen",
      company: "Pacific Import Co.",
      text: "Their data intelligence capabilities have given us insights we never had before. Highly recommended.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-teal-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Your Data. Our Analytics.{' '}
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Your Success.
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-4 leading-relaxed max-w-4xl mx-auto">
              Further unlock efficiency and transparency in every third-party transaction (shipping, raw materials, parts, and services) with our expert data management, analytics, and auditing solutions.
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-4xl mx-auto">
              <strong>You focus on growth and value generation; we'll handle what is important for you to take action.</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-lg px-8">
                  Explore Services
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="text-lg px-8 border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                  Get Free Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Data and Processing Solutions You Can Choose From
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end data management and process outsourcing tailored to your specific business needs, to support execution while maintaining constant visibility on KPI and financial performance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    {service.icon}
                  </div>
                  <CardTitle className="text-xl font-semibold text-gray-900">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-center">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose PBX Solutions?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Enhance your day to day operations with our proven expertise and AI-based solutions.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="mt-12 lg:mt-0">
              <div className="grid grid-cols-2 gap-6">
                <Card className="text-center p-6 border-0 shadow-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {statsLoading ? <Loader2 className="w-8 h-8 animate-spin mx-auto" /> : "10+"}
                  </div>
                  <div className="text-gray-600">Fortune 500 Clients</div>
                </Card>
                <Card className="text-center p-6 border-0 shadow-lg">
                  <div className="text-3xl font-bold text-teal-600 mb-2">
                    {statsLoading ? <Loader2 className="w-8 h-8 animate-spin mx-auto" /> : "30+"}
                  </div>
                  <div className="text-gray-600">Custom KPIs</div>
                </Card>
                <Card className="text-center p-6 border-0 shadow-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-2">Tailored Dashboard</div>
                  <div className="text-gray-600">for real-time actionable information</div>
                </Card>
                <Card className="text-center p-6 border-0 shadow-lg">
                  <div className="text-3xl font-bold text-teal-600 mb-2">
                    {statsLoading ? <Loader2 className="w-8 h-8 animate-spin mx-auto" /> : (stats?.yearsInBusiness || "15+")}
                  </div>
                  <div className="text-gray-600">Years Experience</div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-teal-600 py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Operations?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join others that trust PBX Solutions to handle your transactional data and take your business to the next level. Get started with a free consultation with us today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8">
                Start Free Consultation
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;